import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import AdminMembersReportSurveysTableRow, {
  AdminMembersReportSurveysTableRowSkeleton,
} from "@/admin/insights/member-reports/tabs/contents/AdminMembersReportSurveysTableRow"
import { AdminMembersReportSurveysTablePaginationQuery } from "@/admin/insights/member-reports/tabs/contents/__generated__/AdminMembersReportSurveysTablePaginationQuery.graphql"
import { AdminMembersReportSurveysTableQuery } from "@/admin/insights/member-reports/tabs/contents/__generated__/AdminMembersReportSurveysTableQuery.graphql"
import { AdminMembersReportSurveysTable_PaginationFragment$key } from "@/admin/insights/member-reports/tabs/contents/__generated__/AdminMembersReportSurveysTable_PaginationFragment.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { DiscoTable, DiscoTableSkeletonWithHeader } from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { useState } from "react"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"

const QUESTIONS_PER_PAGE = 10

const headers = [{ value: "Type" }, { value: "Question" }, { value: "Response" }]

interface Props {
  surveyContentUsageId: GlobalID
}

function AdminMembersReportSurveysTable({
  surveyContentUsageId,
}: Props): JSX.Element | null {
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const [activePage, setActivePage] = useState(1)

  const { node } = useLazyLoadQuery<AdminMembersReportSurveysTableQuery>(
    graphql`
      query AdminMembersReportSurveysTableQuery(
        $id: ID!
        $first: Int
        $after: String
        $last: Int
        $before: String
        $memberOrganizationMembershipId: ID!
      ) {
        node(id: $id) {
          ... on ContentUsage {
            id
            ...AdminMembersReportSurveysTable_PaginationFragment
              @arguments(
                first: $first
                after: $after
                last: $last
                before: $before
                contentUsageId: $id
                memberOrganizationMembershipId: $memberOrganizationMembershipId
              )
          }
        }
      }
    `,
    {
      id: surveyContentUsageId,
      first: QUESTIONS_PER_PAGE,
      memberOrganizationMembershipId: queryParams.om!,
    },
    { fetchPolicy: "network-only" }
  )

  const { data, refetch } = usePaginationFragment<
    AdminMembersReportSurveysTablePaginationQuery,
    AdminMembersReportSurveysTable_PaginationFragment$key
  >(
    graphql`
      fragment AdminMembersReportSurveysTable_PaginationFragment on ContentUsage
      @refetchable(queryName: "AdminMembersReportSurveysTablePaginationQuery")
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        last: { type: "Int" }
        before: { type: "String" }
        contentUsageId: { type: "ID!" }
        memberOrganizationMembershipId: { type: "ID!" }
      ) {
        id
        content {
          webForm {
            currentRevision {
              questions(first: $first, after: $after, last: $last, before: $before)
                @connection(key: "AdminMembersReportSurveysTable_questions") {
                totalCount
                edges {
                  cursor
                  node {
                    id
                    ...AdminMembersReportSurveysTableRowFragment
                      @arguments(
                        organizationMembershipId: $memberOrganizationMembershipId
                        contentUsageId: $contentUsageId
                      )
                  }
                }
                pageInfo {
                  endCursor
                  startCursor
                  hasNextPage
                  hasPreviousPage
                }
              }
            }
          }
        }
      }
    `,
    node
  )

  if (!data?.content.webForm?.currentRevision?.questions) return null

  const webFormQuestions = Relay.connectionToArray(
    data.content.webForm.currentRevision.questions
  )
  const cursorsList = data.content.webForm.currentRevision.questions.edges.map(
    (e) => e.cursor
  )

  return (
    <DiscoTable
      activePage={activePage}
      setActivePage={setActivePage}
      rowsPerPage={QUESTIONS_PER_PAGE}
      scrollable
      testid={"AdminMembersReportSurveysTable"}
      header={headers}
      rows={webFormQuestions.map((webFormQuestion) => (
        <AdminMembersReportSurveysTableRow
          key={webFormQuestion.id}
          webFormQuestionKey={webFormQuestion}
        />
      ))}
      connection={{
        cursorsList,
        totalCount: data.content.webForm.currentRevision.questions.totalCount,
        pageInfo: {
          endCursor: data.content.webForm.currentRevision.questions.pageInfo.endCursor,
          startCursor:
            data.content.webForm.currentRevision.questions.pageInfo.startCursor,
        },
      }}
      onPaginate={refetch}
    />
  )
}

function AdminMembersReportSurveysTableSkeleton() {
  return (
    <DiscoTableSkeletonWithHeader
      header={headers}
      rows={QUESTIONS_PER_PAGE}
      row={<AdminMembersReportSurveysTableRowSkeleton />}
    />
  )
}

export default Relay.withSkeleton({
  component: AdminMembersReportSurveysTable,
  skeleton: AdminMembersReportSurveysTableSkeleton,
})
